// @ts-strict-ignore
import {
  Component,
  ComponentFactoryResolver,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  SimpleChange,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { Observable, of } from 'rxjs';

import { LMListGridComponent } from './lm-list-grid.component';
import { LMListTreeComponent } from './lm-list-tree.component';
import { LMListCubeViewerComponent } from './lm-list-cube-viewer.component';
import { LMListViewComponent } from './lm-list-view.component';
import { LMListService } from '@list/services/lm-list.service';
import { LMListCalendarComponent } from './lm-list-calendar.component';
import { LMLookupComponent } from '@field/lookup';
import { AppConstants } from '@app/utils';

@Component({
  selector: 'lm-list-content',
  template: '<div #listContainer></div>',
  styleUrls: [ 'lm-list-content.component.scss' ]
})
export class LMListContentComponent implements LMListContentInterface, OnInit, OnChanges {
  @ViewChild('listContainer', { read: ViewContainerRef, static: true }) listContainer: ViewContainerRef;

  @Input() displayType: string;
  @Input() listUrlInfo: LMListUrlInfo;
  @Input() listSpec: LMListSpec;
  @Input() listState: LMListState;
  @Input() listPersonalization: LMListPersonalization;
  @Input() fromDataView: LMDataView;
  @Input() sortOrder: LMListSortOrder;
  @Input() filterCriteria: Array<LMFilterFieldCriteria>;
  @Input() columnfilterOperators: Map<string, string>;
  @Input() filterListFilterCriteria: Array<LMFilterFieldCriteria>;
  @Input() hasFilterList: boolean;
  @Input() showFilter: boolean;
  @Input() implementsDragAndDrop: boolean;
  @Input() implementsCartView: boolean;
  @Input() selectRequestData: SelectRequestData;
  @Input() chartName: string;
  @Input() disabled = false;
  @Input() isDashBoardContent = false;
  @Input() implementsManualLoad: boolean;
  @Input() manualLoadInfo: LMManualLoadInfo;
  @Input() inSelectListEditMode: boolean;
  @Input() multiSelectedValues: Array<LMMultiSelectedValue>;

  @HostBinding('style.height') get hostHeight() {
    if (this.listSpec && this.listSpec.busListSpec && this.listSpec.busListSpec.implementsOrgChartView) {
      return 'auto';
    }
  }

  private holdSelectedRows: Array<number>;
  private component: LMListContentInterface;

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private lmListService: LMListService
  ) {}

  ngOnInit() {
    this.buildComponent();
  }

  ngOnChanges(changes: {[propKey: string]: SimpleChange}) {
    for (const propKey of Object.keys(changes)) {
      const changedInput = changes[propKey];
      // Ignore first change done from OnInit
      if (changedInput.isFirstChange()) {
        break;
      }
      // Rebuild component
      // displayType changes when the user switches between chart or list.
      // listSpec changes when period view changes in cube viewer.
      if (propKey === 'displayType' || propKey === 'listSpec') {
        this.buildComponent();
        break;
      }

      this.component[propKey] = changedInput.currentValue;
    }
  }

  private buildComponent() {
    this.listContainer.clear();
    const component = this.lmListService.getListByDisplayType(this.displayType);
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(component);
    const componentRef = this.listContainer.createComponent(componentFactory);
    this.component = <LMListContentInterface>componentRef.instance;

    // Set required component inputs first so any that call methods
    // will have what they need available.
    // todo ppatton ben remove the method calls from input setters and place in ngOnChanges maybe.
    // this.listState must be set before this.listSpec
    this.component.listState = this.listState;
    this.component.listUrlInfo = this.listUrlInfo;
    this.component.listSpec = this.listSpec;
    this.component.listPersonalization = this.listPersonalization;
    this.component.inSelectListEditMode = this.inSelectListEditMode;

    // Set component inputs
    this.component.hasFilterList = this.hasFilterList;
    this.component.implementsDragAndDrop = this.implementsDragAndDrop;
    this.component.implementsCartView = this.implementsCartView;
    this.component.selectRequestData = this.selectRequestData;
    this.component.isDashBoardContent = this.isDashBoardContent;
    this.component.implementsManualLoad = this.implementsManualLoad;
    this.component.manualLoadInfo = this.manualLoadInfo;

    // Inputs having setters that executes some method:
    this.component.disabled = this.disabled;
    this.component.showFilter = this.showFilter;
    this.component.filterCriteria = this.filterCriteria;
    this.component.columnfilterOperators = this.columnfilterOperators;
    this.component.filterListFilterCriteria = this.filterListFilterCriteria;
    this.component.chartName = this.chartName;
    this.component.fromDataView = this.fromDataView;
    this.component.sortOrder = this.sortOrder;
    this.component.multiSelectedValues = this.multiSelectedValues;
  }

  public clearData() {
    if (this.isType('list')) {
      this.listGrid.clearData();
    } else if (this.isType('tree')) {
      this.listTree.clearData();
    } else if (this.isType('listView')) {
      // (this.component as LMListViewComponent).resetData(); FutureSprint oct 25 1
    } else if (this.isType('calendar')) {
      // (this.component as LMListCalendarComponent).resetData(); FutureSprint oct 25 1
    } else if (this.isType('cubeView')) {
      // (this.component as LMListCubeViewerComponent).resetData(); FutureSprint oct 25 1
    }
  }

  public sort(sortOrder: LMListSortOrder): void {
    if (this.isType('list')) {
      this.listGrid.sortPage(sortOrder);
    } else if (this.isType('listView')) {
      (this.component as LMListViewComponent).sortPage(sortOrder);
    }
  }

  public columnById(id: string): ListGridColumn {
    if (this.isType('list')) {
      return this.listGrid.columnById(id);
    }
    if (this.isType('tree')) {
      return this.listTree.columnById(id);
    }
    return null;
  }

  public filterCriteriaForDefaultFilter(): LMFilterFieldCriteria[] {
    if (this.isType('list')) {
      return this.listGrid.filterCriteriaForDefaultFilter();
    }
    if (this.isType('tree')) {
      return this.listTree.filterCriteriaForDefaultFilter();
    }
    return null;
  }

  public get savedSearches(): LMDataViewSet {
    return this.component.savedSearches;
  }

  public multiSelectedValuesChanged(): void {
    this.component.multiSelectedValuesChanged();
  }

  public disableSaveInUserListViewMenu(): boolean {
    return this.component.disableSaveInUserListViewMenu();
  }

  public clearUserListView(): void {
    this.component.clearUserListView();
  }

  public get sohoFilterConditions(): Array<SohoDataGridFilterCondition> {
    if (this.isType('list')) {
      return this.listGrid.sohoFilterConditions;
    }
    if (this.isType('tree')) {
      return this.listTree.sohoFilterConditions;
    }
    return [];
  }

  public getSelectedTreeDataView(): LMListDataView {
    if (this.displayType === 'tree') {
      return this.listTree.getSelectedTreeDataView();
    }
    if (this.displayType === 'cubeView') {
      return (<LMListCubeViewerComponent>this.component).getSelectedTreeDataView();
    }
    return null;
  }

  public clearFilter() {
    if (this.displayType === 'list') {
      this.listGrid.sohoDataGridComponent.clearFilter();
    } else if (this.displayType === 'tree') {
      this.listTree.sohoDataGridComponent.clearFilter();
    }
  }

  public applyFilter() {
    if (this.displayType === 'list') {
      this.listGrid.sohoDataGridComponent.applyFilter();
    } else if (this.displayType === 'tree') {
      this.listTree.sohoDataGridComponent.applyFilter();
    }
  }

  public isType(type: string): boolean {
    return (this.displayType === type);
  }

  public getDataViewSet(): LMDataViewSet {
    return this.component ? this.component.getDataViewSet() : null;
  }

  public getSelectedRows(): number[] {
    return this.component ? this.component.getSelectedRows() : [ -1 ];
  }

  public getSelectedRow(): number {
    return this.component ? this.component.getSelectedRow() : -1;
  }

  public getDirtyRows(commit: boolean): Array<LMDataView> {
    return this.component ? this.component.getDirtyRows(commit) : null;
  }

  public isModified(): boolean {
    return this.component ? this.component.isModified() : false;
  }

  public setDirtyIndicator(row: number, cell: number, showDirty: boolean, data?: any) {
    if (this.component) {
      this.component.setDirtyIndicator(row, cell, showDirty, data);
    }
  }

  public clearDirtyCell(row: number, cell: number): void {
    if (this.isType('list')) {
      this.listGrid.sohoDataGridComponent.clearDirtyCell(row, cell);
    } else if (this.isType('tree')) {
      this.listTree.sohoDataGridComponent.clearDirtyCell(row, cell);
    }
  }

  public onContextMenuItemSelected(actionReference: LMBusActionReference, selectedDataView?: LMDataView) {
    if (this.component) {
      this.component.onContextMenuItemSelected(actionReference, selectedDataView);
    }
  }

  public resetData(): void {
    if (this.isType('list')) {
      this.listGrid.resetData();
    } else if (this.isType('tree')) {
      this.listTree.refresh();
    } else if (this.isType('listView')) {
      (this.component as LMListViewComponent).refresh();
    } else if (this.isType('calendar')) {
      (this.component as LMListCalendarComponent).refresh();
    } else if (this.isType('cubeView')) {
      (this.component as LMListCubeViewerComponent).refresh();
    }
  }

  public refresh(): Observable<boolean> {
    return this.component ? this.component.refresh() : of(true);
  }

  public activatedRow(): number {
    return this.component ? this.component.activatedRow() : -1;
  }

  public updateDataView(key: number, dataView: LMDataView) {
    if (this.component) {
      this.component.updateDataView(key, dataView);
    }
  }

  public updateDataViewTransaction(key: number, status: string, message: string) {
    if (this.component) {
      this.component.updateDataViewTransaction(key, status, message);
    }
  }

  public setSelectedRows(rowsToSelect: Array<number>, scrollIntoView?: boolean) {
    if (this.component) {
      this.component.setSelectedRows(rowsToSelect, scrollIntoView);
    }
  }

  public unSelectAllRows() {
    if (this.component) {
      this.component.unSelectAllRows();
    }
  }

  public unSelectRow(rowToUnselect: number) {
    if (this.listSpec.busListSpec.selection === 'disabled') {
      return;
    }

    if (this.isType('list')) {
      this.listGrid.sohoDataGridComponent.unselectRow(rowToUnselect);
    } else if (this.isType('tree')) {
      this.listTree.sohoDataGridComponent.unselectRow(rowToUnselect);
    }
  }

  public activateRow(rowToActivate: number, scrollIntoView?: boolean) {
    if (this.component) {
      this.component.activateRow(rowToActivate, scrollIntoView);
    }
  }

  public updated(): void {
    // need to implement
  }

  public addDataView(dataView: LMDataView, index: number) {
    if (this.component) {
      this.component.addDataView(dataView, index);
    }
  }

  public removeDataView(index: number) {
    if (this.component) {
      this.component.removeDataView(index);
    }
  }

  public get listGrid(): LMListGridComponent {
    if (this.displayType === 'list') {
      return <LMListGridComponent> this.component;
    }
    return null;
  }

  public get listTree(): LMListTreeComponent {
    if (this.displayType === 'tree') {
      return <LMListTreeComponent> this.component;
    }
    return null;
  }

  public getColumns(): Array<ListGridColumn> {
    if (this.isType('list')) {
      return this.listGrid.getColumns();
    } else if (this.isType('tree')) {
      return this.listTree.getColumns();
    }
  }

  public clearDirty() {
    if (this.isType('list')) {
      return this.listGrid.clearDirty();
    } else if (this.isType('tree')) {
      return this.listTree.clearDirty();
    } else if (this.isType('cubeView')) {
      return (<LMListCubeViewerComponent>this.component).clearDirty();
    }
  }

  public rowStatus(row: number, status: string, tooltip: string) {
    if (this.isType('list')) {
      this.listGrid.rowStatus(row, status, tooltip);
    } else if (this.isType('tree')) {
      this.listTree.rowStatus(row, status, tooltip);
    }
  }

  public commitCellEdit(): void {
    if (this.isType('list')) {
      this.listGrid.commitCellEdit();
    } else if (this.isType('tree')) {
      this.listTree.commitCellEdit();
    }
  }

  public setActiveCell(row: number, cell: number) {
    if (this.listGrid) {
      this.listGrid.setActiveCell(row, cell);
    }
  }

  public removeRow(row: number) {
    if (this.listGrid) {
      this.listGrid.removeRow(row);
    }
  }

  public get hasFilterConditions(): boolean {
    if (this.displayType === 'list') {
      return (this.listGrid.sohoDataGridComponent.filterConditions().length > 0);
    } else if (this.displayType === 'tree') {
      return (this.listTree.sohoDataGridComponent.filterConditions().length > 0);
    }
    return false;
  }

  public get lmFilterCriteria(): Array<LMFilterFieldCriteria> {
    if (this.displayType === 'list') {
      return this.listGrid.lmFilterCriteria;
    } else if (this.displayType === 'tree') {
      return this.listTree.lmFilterCriteria;
    }
    return [];
  }

  public setSelectListFocus(): void {
    if (this.isType('list')) {
      this.listGrid.setSelectListFocus();
    } else if (this.isType('tree')) {
      this.listTree.setSelectListFocus();
    } else if (this.isType('listView')) {
      (<LMListViewComponent>this.component).setSelectListFocus();
    }
  }

  public showSelectAllCheckBox(showCheckBoxInHeader: boolean): void {
    if (this.isType('list')) {
      this.listGrid.showSelectAllCheckBox(showCheckBoxInHeader);
    } else if (this.isType('tree')) {
      this.listTree.showSelectAllCheckBox(showCheckBoxInHeader);
    }
  }

  public get listCalendar(): LMListCalendarComponent {
    if (this.displayType === 'calendar') {
      return <LMListCalendarComponent> this.component;
    }
    return null;
  }

  public cubeSearchOpenLookup(field: LMLookupComponent, searchFormDataView: LMDataView) {
    if (this.displayType === 'cubeView') {
      (<LMListCubeViewerComponent>this.component).cubeSearchOpenLookup(field, searchFormDataView);
    }
  }

  public scrollNext() {
    if (typeof this.component.scrollNext === 'function') {
      this.component.scrollNext();
    }
  }

  public scrollPrevious() {
    if (typeof this.component.scrollPrevious === 'function') {
      this.component.scrollPrevious();
    }
  }

  public scrollIsStart(): boolean {
    if (typeof this.component.scrollIsStart === 'function') {
      return this.component.scrollIsStart();
    }
    return false;
  }

  public async scrollIsStartPromise(): Promise<boolean> {
    if (typeof this.component.scrollIsStartPromise === 'function') {
      return await this.component.scrollIsStartPromise();
    }

    return false;
  }

  public async scrollIsEndPromise(): Promise<boolean> {
    if (typeof this.component.scrollIsEndPromise === 'function') {
      return await this.component.scrollIsEndPromise();
    }

    return false;
  }

  public scrollIsEnd(): boolean {
    if (typeof this.component.scrollIsEnd === 'function') {
      return this.component.scrollIsEnd();
    }
    return false;
  }

  public hasOverflow(): boolean {
    if (typeof this.component.hasOverflow === 'function') {
      return this.component.hasOverflow();
    }
    return false;
  }

  // Resets scroll position to start on horizontal scroller component
  // Need to do this when data is reloaded, or if record has been changed via next/previous page events
  public resetScrollPosition(itemCount?: number) {
    if (typeof this.component.resetScrollPosition === 'function') {
      return this.component.resetScrollPosition(itemCount);
    }
  }
}
